import { ChangeEvent, useRef, useState } from 'react';
import { Table, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { ConnectedProps, connect } from 'react-redux';
import type { ICostCodesItem, TCostCodesFilters } from 'src/common/interfaces/cost-codes';
import {
  addCostCodesItem,
  costCodesCellValueUpdated,
  deleteCostSetupItem as deleteCostCodesItem,
  initialCostCodesItem,
  updateCostCodesItem,
} from 'src/store/entities/cost-codes/cost-codes';
import {
  selectCostCodesError,
  selectCostCodesIsAdding,
  selectCostCodesIsFetching,
  selectCostCodesIsUpdating,
} from 'src/store/entities/cost-codes/selectors';
import translations from 'src/internationalisation/translation-map.json';
import type { TFunction } from 'react-i18next';
import { debounce } from 'lodash';
import { autoSaveWaitShort } from 'src/config/config';
import type { TRootState } from 'src/store/store-types';
import { getRows } from './utils';

type TCostCodesTableProps = PropsFromRedux & {
  data: ICostCodesItem[];
  isCategory?: boolean;
  t: TFunction;
};

const CostCodesTable = ({
  data,
  isAdding,
  isUpdating,
  deleteCostCodesItem,
  costCodesCellValueUpdated,
  isCategory = false,
  addCostCodesItem,
  t,
  updateCostCodesItem,
  costCodesError,
}: TCostCodesTableProps) => {
  const [filters, setFilters] = useState<Partial<TCostCodesFilters>>({});
  const debounceUpdatedCostCodesItem = useRef(debounce(updateCostCodesItem, autoSaveWaitShort));

  const onClickDelete = (item: ICostCodesItem) => {
    const { costCodesId } = item;
    if (costCodesId) {
      deleteCostCodesItem(costCodesId);
    }
  };

  const rows = getRows(data, filters, costCodesCellValueUpdated, debounceUpdatedCostCodesItem, onClickDelete);

  if (costCodesError !== null) {
    rows.forEach((row) => {
      row.cells.forEach((cell) => {
        if (cell.name === costCodesError.costCodesId) {
          cell.error = t(translations.costCodes_costCodesItemCannotHaveDuplicateNames);
        }
      });
    });
  }

  const headers = isCategory
    ? [
        { value: t(translations.settings_costCategoryName), label: 'name' },
        { value: t(translations.code), label: 'code' },
      ]
    : [
        { value: t(translations.settings_costItemName), label: 'name' },
        { value: t(translations.code), label: 'code' },
      ];

  const table = {
    headers: [
      {
        actions: [
          {
            icon: 'add',
            label: 'Add',
            onClick: () =>
              addCostCodesItem({
                ...initialCostCodesItem,
                isCategory,
                name: `${initialCostCodesItem.name}-${data.filter((item) => item.deleted === null).length}-${
                  isCategory ? t(translations.settings_category) : t(translations.costSetup_item)
                }`,
              }),
            primary: true,
            disabled: isAdding || isUpdating,
          },
        ],
        cells: headers,
      },
      {
        cells: headers.map(({ label }) => ({
          type: 'Input',
          placeholder: 'Filter',
          value: filters[label as keyof TCostCodesFilters] || '',
          onChange: (ev: ChangeEvent<HTMLInputElement>) =>
            setFilters({
              ...filters,
              [label]: ev.target.value,
            }),
        })),
      },
    ],
    rows,
  };

  return <Table table={table} />;
};

const mapStateToProps = (state: TRootState) => ({
  isAdding: selectCostCodesIsAdding(state),
  isFetching: selectCostCodesIsFetching(state),
  isUpdating: selectCostCodesIsUpdating(state),
  costCodesError: selectCostCodesError(state),
});

const mapDispatchToProps = {
  addCostCodesItem,
  costCodesCellValueUpdated,
  deleteCostCodesItem,
  updateCostCodesItem,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(CostCodesTable));

export { Container as CostCodesTable };
